import { hasConsent } from '@/lib/consent';
import { useEffect, useState } from 'react';
import { isDoNotTrack } from '@/lib/doNotTrack';
import { isAppIdle } from '@/hooks/useAfterIdle';

const PULSE_URL = `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/pulse`;

const isPulseTrackingEnabled = (): boolean => {
  // Disabled by default: ad blockers flag the pulse endpoint and Lighthouse
  // surfaces ERR_BLOCKED_BY_CLIENT as a console error even for swallowed errors.
  if (!hasConsent('statistics')) return false;
  return sessionStorage.getItem('enable-pulse-tracking') === 'true';
};

/**
 * Send a pulse payload via sendBeacon (or keepalive fetch fallback).
 * Avoids loading the supabase auth-js bundle on the critical path and
 * never blocks the main thread.
 */
const sendPulseBeacon = (payload: object): boolean => {
  if (!isPulseTrackingEnabled()) return true;
  if (!PULSE_URL) return false;
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(PULSE_URL, blob)) {
      return true;
    }
  } catch {
    // fall through to fetch
  }
  try {
    fetch(PULSE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
};

const SESSION_STORAGE_KEY = 'klanert-session-id';
const SESSION_EXPIRY_KEY = 'klanert-session-expiry';
const SESSION_START_FIRED_KEY = 'klanert-session-start-fired';
const SESSION_LIFETIME_DAYS = 30;

/**
 * Generates a random session ID (UUID v4 format)
 */
const generateSessionId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Gets the current timestamp in milliseconds
 */
const now = (): number => Date.now();

/**
 * Calculates expiry timestamp (30 days from now)
 */
const getExpiryTimestamp = (): number => {
  return now() + (SESSION_LIFETIME_DAYS * 24 * 60 * 60 * 1000);
};

/**
 * Checks if the session has expired
 */
const isSessionExpired = (expiryTimestamp: number | null): boolean => {
  if (!expiryTimestamp) return true;
  return now() > expiryTimestamp;
};

/**
 * Parses user agent to detect device type
 */
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const ua = navigator.userAgent.toLowerCase();
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|android|iphone|ipod|blackberry|opera mini|iemobile|wpdesktop/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

/**
 * Parses user agent to detect operating system
 */
const getOperatingSystem = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
};

/**
 * Parses user agent to detect browser
 */
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Unknown';
};

/**
 * Extracts UTM parameters from URL
 */
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_term: params.get('utm_term') || undefined,
    gclid: params.get('gclid') || undefined,
  };
};

/**
 * Gets or creates a session ID from localStorage
 */
// In-memory session ID used while there is no statistics consent.
let memorySessionId: string | null = null;
const getMemorySessionId = (): string => {
  if (!memorySessionId) memorySessionId = generateSessionId();
  return memorySessionId;
};

export const getSessionId = (): string => {
  // Without statistics consent: never touch localStorage (§ 25 TDDDG)
  if (!hasConsent('statistics')) return getMemorySessionId();
  try {
    const storedSessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    const storedExpiry = localStorage.getItem(SESSION_EXPIRY_KEY);
    const expiryTimestamp = storedExpiry ? parseInt(storedExpiry, 10) : null;

    // If we have a valid, non-expired session, reuse it
    if (storedSessionId && !isSessionExpired(expiryTimestamp)) {
      // Refresh expiry on each visit
      localStorage.setItem(SESSION_EXPIRY_KEY, getExpiryTimestamp().toString());
      return storedSessionId;
    }

    // Generate new session
    const newSessionId = generateSessionId();
    localStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
    localStorage.setItem(SESSION_EXPIRY_KEY, getExpiryTimestamp().toString());
    // Reset session_start fired flag for new session
    localStorage.removeItem(SESSION_START_FIRED_KEY);
    
    return newSessionId;
  } catch (error) {
    console.warn('localStorage not available, generating ephemeral session ID');
    return generateSessionId();
  }
};

/**
 * Checks if session_start event has already been fired for this session
 */
const hasSessionStartFired = (): boolean => {
  try {
    return localStorage.getItem(SESSION_START_FIRED_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Marks session_start as fired
 */
const markSessionStartFired = (): void => {
  if (!hasConsent('statistics')) return;
  try {
    localStorage.setItem(SESSION_START_FIRED_KEY, 'true');
  } catch {
    // Ignore
  }
};

/**
 * Fires the session_start tracking event
 */
const fireSessionStartEvent = async (sessionId: string): Promise<void> => {
  // Skip if DNT is enabled or already fired
  if (isDoNotTrack() || !hasConsent('statistics')) return;
  
  if (hasSessionStartFired()) {
    return;
  }

  const utmParams = getUTMParams();
  
  const payload = {
    event_type: 'session_start',
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    entry_url: window.location.href,
    referrer: document.referrer || undefined,
    utm_source: utmParams.utm_source,
    utm_medium: utmParams.utm_medium,
    utm_campaign: utmParams.utm_campaign,
    utm_content: utmParams.utm_content,
    utm_term: utmParams.utm_term,
    gclid: utmParams.gclid,
    device_type: getDeviceType(),
    operating_system: getOperatingSystem(),
    browser: getBrowser(),
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  // Use sendBeacon to keep this off the LCP critical path and avoid
  // pulling the auth-js bundle into the initial network waterfall.
  if (sendPulseBeacon(payload)) {
    markSessionStartFired();
  }
};

/**
 * Sends a custom tracking event
 */
export const trackEvent = async (
  eventType: string, 
  additionalPayload?: Record<string, unknown>
): Promise<void> => {
  // Skip if DNT is enabled
  if (isDoNotTrack() || !hasConsent('statistics')) return;
  
  const sessionId = getSessionId();
  
  const payload = {
    event_type: eventType,
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    payload: additionalPayload,
  };

  try {
    sendPulseBeacon(payload);
  } catch (error) {
    console.error(`Error sending ${eventType} event:`, error);
  }
};

/**
 * Clears the current session (for testing or logout)
 */
export const clearSession = (): void => {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(SESSION_EXPIRY_KEY);
    localStorage.removeItem(SESSION_START_FIRED_KEY);
  } catch (error) {
    console.warn('Failed to clear session');
  }
};

/**
 * React hook for session tracking
 * Initializes session and fires session_start event on first load
 */
export const useSessionTracking = () => {
  const [sessionId, setSessionId] = useState<string>('');

  useEffect(() => {
    const id = getSessionId();
    setSessionId(id);
    
    // Expose globally for non-React tracking code
    if (typeof window !== 'undefined') {
      (window as any).__klanertSessionId = id;
    }

    // Fire session_start strictly after the browser has settled past LCP.
    // We wait for `load` (or already-idle state) and then schedule via
    // requestIdleCallback so the request never lands on the critical path.
    const fire = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
      if (typeof ric === 'function') {
        ric(() => fireSessionStartEvent(id), { timeout: 4000 });
      } else {
        setTimeout(() => fireSessionStartEvent(id), 1500);
      }
    };
    if (isAppIdle() || document.readyState === 'complete') {
      fire();
    } else {
      window.addEventListener('load', fire, { once: true });
    }
  }, []);

  return { sessionId, trackEvent };
};

// Expose session ID globally. Without statistics consent this is an
// in-memory ID only — nothing is written to localStorage.
if (typeof window !== 'undefined') {
  (window as any).__klanertSessionId = getSessionId();
}

export default useSessionTracking;
